import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api, qs } from '../api';
import { Icon } from '../components/icons';
import { Empty, EntryStatusBadge, ErrorBox, EventStatusBadge, Field, ItemStatusBadge, Loading, Modal, PageHead, Tabs } from '../components/ui';
import { fmtDate, fmtDateTime, fmtDistance, fmtHours, fmtTime } from '../format';
import { useI18n } from '../i18n';
import type { EventDetail, EventStatus, Item, Kit, PackingRow, TimeEntry, User } from '../types';
import { EventFormModal } from './Events';

const NEXT: Record<EventStatus, { to: EventStatus; key: string; tone?: string }[]> = {
  draft: [{ to: 'confirmed', key: 'events.action.confirm', tone: 'btn-primary' }, { to: 'cancelled', key: 'events.action.cancel', tone: 'btn-danger' }],
  confirmed: [
    { to: 'completed', key: 'events.action.complete' },
    { to: 'draft', key: 'events.action.toDraft' },
    { to: 'cancelled', key: 'events.action.cancel', tone: 'btn-danger' },
  ],
  in_progress: [{ to: 'completed', key: 'events.action.complete', tone: 'btn-primary' }],
  completed: [{ to: 'invoiced', key: 'events.action.invoiced', tone: 'btn-primary' }, { to: 'in_progress', key: 'events.action.reopen' }],
  invoiced: [],
  cancelled: [{ to: 'draft', key: 'events.action.restore' }],
};

type Tab = 'overview' | 'equipment' | 'crew' | 'hours' | 'scans';

export function EventDetailPage() {
  const { id = '' } = useParams();
  const { t, lang } = useI18n();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('overview');
  const [editing, setEditing] = useState(false);
  const ev = useQuery({ queryKey: ['event', id], queryFn: () => api.get<EventDetail>(`/events/${id}`) });
  const changeStatus = useMutation({
    mutationFn: (status: EventStatus) => api.post(`/events/${id}/status`, { status }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['event', id] });
      qc.invalidateQueries({ queryKey: ['events'] });
      qc.invalidateQueries({ queryKey: ['overview'] });
    },
  });
  if (ev.isLoading) return <Loading />;
  if (!ev.data) return <ErrorBox error={ev.error} />;
  const e = ev.data;
  const closed = ['completed', 'invoiced', 'cancelled'].includes(e.status);

  return (
    <>
      <PageHead
        back={{ to: '/events', label: t('nav.events') }}
        title={
          <span className="row" style={{ gap: 10 }}>
            {e.title} <EventStatusBadge status={e.status} />
          </span>
        }
        sub={`${fmtDate(e.setupStart, lang)} · ${e.venue.name}${e.client ? ` · ${e.client.name}` : ''}`}
        actions={
          <>
            {NEXT[e.status].map((a) => (
              <button
                key={a.to}
                className={`btn ${a.tone ?? ''}`}
                disabled={changeStatus.isPending}
                onClick={() => {
                  if (a.to === 'cancelled' && !window.confirm(t('events.confirmCancel'))) return;
                  changeStatus.mutate(a.to);
                }}
              >
                {t(a.key)}
              </button>
            ))}
            {!closed && <button className="btn" onClick={() => setEditing(true)}>{t('common.edit')}</button>}
          </>
        }
      />
      <ErrorBox error={changeStatus.error} />
      <Tabs<Tab>
        value={tab}
        onChange={setTab}
        tabs={[
          { key: 'overview', label: t('events.tab.overview') },
          { key: 'equipment', label: t('events.tab.equipment'), count: e.equipment.total },
          { key: 'crew', label: t('events.tab.crew'), count: e.crew.length },
          { key: 'hours', label: t('events.tab.hours') },
          { key: 'scans', label: t('events.tab.scans') },
        ]}
      />
      {tab === 'overview' && <Overview e={e} onTab={setTab} />}
      {tab === 'equipment' && <EquipmentTab e={e} />}
      {tab === 'crew' && <CrewTab e={e} />}
      {tab === 'hours' && <HoursTab eventId={e.id} />}
      {tab === 'scans' && <ScansTab eventId={e.id} />}
      {editing && <EventFormModal event={e} onClose={() => setEditing(false)} />}
    </>
  );
}

function Overview({ e, onTab }: { e: EventDetail; onTab: (t: Tab) => void }) {
  const { t, lang } = useI18n();
  const lead = e.crew.find((c) => c.role === 'lead');
  const problems = [
    e.crew.length === 0 && t('events.problem.no_crew'),
    e.requiresLead && !lead && t('events.problem.no_lead'),
    e.equipment.total === 0 && t('events.problem.no_equipment'),
  ].filter(Boolean) as string[];
  const pct = e.equipment.total ? Math.round(((e.equipment.out + e.equipment.returned) / e.equipment.total) * 100) : 0;
  return (
    <div className="stack">
      {problems.length > 0 && !['completed', 'invoiced', 'cancelled'].includes(e.status) && (
        <div className="alert alert-warn">
          <strong>{t('events.notReady')}</strong>
          <ul>{problems.map((p) => <li key={p}>{p}</li>)}</ul>
        </div>
      )}
      <div className="grid grid-2">
        <div className="card card-pad stack">
          <h2>{t('events.schedule')}</h2>
          <dl className="kv">
            <dt>{t('events.setupStart')}</dt><dd className="num">{fmtDateTime(e.setupStart, lang)}</dd>
            <dt>{t('events.eventStart')}</dt><dd className="num">{fmtDateTime(e.eventStart, lang)}</dd>
            <dt>{t('events.eventEnd')}</dt><dd className="num">{fmtDateTime(e.eventEnd, lang)}</dd>
            <dt>{t('events.teardownEnd')}</dt><dd className="num">{fmtDateTime(e.teardownEnd, lang)}</dd>
            <dt>{t('events.languages')}</dt><dd>{e.languages.join(' · ') || '–'}</dd>
            <dt>{t('events.delegates')}</dt><dd className="num">{e.delegates ?? '–'}</dd>
            {e.notes && (<><dt>{t('common.notes')}</dt><dd style={{ whiteSpace: 'pre-line' }}>{e.notes}</dd></>)}
          </dl>
        </div>
        <div className="stack">
          <div className="card card-pad stack">
            <h2>{t('events.venueAndClient')}</h2>
            <dl className="kv">
              <dt>{t('events.venue')}</dt>
              <dd>
                {e.venue.name}
                {e.venue.address && <div className="small muted">{e.venue.address}</div>}
                <div className="small muted">{t('venues.geofence')}: {e.venue.geofenceRadiusM} m</div>
              </dd>
              <dt>{t('events.client')}</dt>
              <dd>
                {e.client ? (
                  <>
                    {e.client.name}
                    {e.client.contactName && <div className="small">{e.client.contactName}</div>}
                    {e.client.contactPhone && <div className="small"><a href={`tel:${e.client.contactPhone}`}>{e.client.contactPhone}</a></div>}
                  </>
                ) : (
                  '–'
                )}
              </dd>
            </dl>
          </div>
          <div className="card card-pad stack">
            <div className="spread">
              <h2>{t('events.tab.equipment')}</h2>
              <button className="btn btn-sm" onClick={() => onTab('equipment')}>{t('common.open')}</button>
            </div>
            <div className="progress" aria-hidden="true"><span style={{ width: `${pct}%` }} /></div>
            <div className="row small">
              <span className="badge">{t('packing.pending')}: {e.equipment.pending}</span>
              <span className="badge badge-blue">{t('packing.out')}: {e.equipment.out}</span>
              <span className="badge badge-green">{t('packing.returned')}: {e.equipment.returned}</span>
            </div>
            {e.loadoutConfirmedAt && <div className="small muted">{t('events.loadoutConfirmed', { when: fmtDateTime(e.loadoutConfirmedAt, lang) })}</div>}
          </div>
          <div className="card card-pad stack">
            <div className="spread">
              <h2>{t('events.tab.crew')}</h2>
              <button className="btn btn-sm" onClick={() => onTab('crew')}>{t('common.open')}</button>
            </div>
            {e.crew.length === 0 ? (
              <span className="muted">{t('events.noCrew')}</span>
            ) : (
              <div className="row">
                {e.crew.map((c) => (
                  <span key={c.userId} className={`badge ${c.role === 'lead' ? 'badge-ink' : ''}`}>
                    {c.name}{c.role === 'lead' ? ` · ${t('crewRole.lead')}` : ''}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function EquipmentTab({ e }: { e: EventDetail }) {
  const t = useI18n().t;
  const qc = useQueryClient();
  const [booking, setBooking] = useState(false);
  const closed = ['completed', 'invoiced', 'cancelled'].includes(e.status);
  const list = useQuery({ queryKey: ['packing', e.id], queryFn: () => api.get<{ items: PackingRow[] }>(`/events/${e.id}/packing-list`) });
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['packing', e.id] });
    qc.invalidateQueries({ queryKey: ['event', e.id] });
    qc.invalidateQueries({ queryKey: ['events'] });
    qc.invalidateQueries({ queryKey: ['items'] });
  };
  const removeItem = useMutation({ mutationFn: (itemId: string) => api.del(`/events/${e.id}/bookings/items/${itemId}`), onSuccess: invalidate });
  const removeKit = useMutation({ mutationFn: (kitId: string) => api.del(`/events/${e.id}/bookings/kits/${kitId}`), onSuccess: invalidate });

  // Group rows by case; loose items last.
  const groups = useMemo(() => {
    const m = new Map<string, { kitId: string | null; kitName: string | null; rows: PackingRow[] }>();
    for (const r of list.data?.items ?? []) {
      const k = r.kitId ?? '_loose';
      if (!m.has(k)) m.set(k, { kitId: r.kitId, kitName: r.kitName, rows: [] });
      m.get(k)!.rows.push(r);
    }
    return [...m.values()].sort((a, b) => (a.kitId ? 0 : 1) - (b.kitId ? 0 : 1));
  }, [list.data]);

  return (
    <div className="stack">
      <div className="spread">
        <p className="muted" style={{ margin: 0 }}>{t('events.equipmentHint')}</p>
        {!closed && (
          <button className="btn btn-primary" onClick={() => setBooking(true)}>
            <Icon.plus />
            {t('events.bookEquipment')}
          </button>
        )}
      </div>
      <ErrorBox error={removeItem.error ?? removeKit.error} />
      {list.isLoading ? (
        <Loading />
      ) : groups.length === 0 ? (
        <div className="card"><Empty title={t('events.noEquipment')} text={t('events.noEquipmentText')} /></div>
      ) : (
        groups.map((g) => (
          <div className="card" key={g.kitId ?? 'loose'}>
            <div className="card-head">
              <h3>{g.kitId ? `${g.kitName}` : t('events.looseItems')} <span className="muted">· {g.rows.length}</span></h3>
              {g.kitId && !closed && (
                <button className="btn btn-sm btn-ghost" onClick={() => removeKit.mutate(g.kitId!)} disabled={removeKit.isPending}>{t('events.removeCase')}</button>
              )}
            </div>
            <div className="table-wrap">
              <table className="table">
                <tbody>
                  {g.rows.map((r) => (
                    <tr key={r.itemId}>
                      <td className="mono nowrap" style={{ width: 120 }}>{r.qrCode}</td>
                      <td><Link to={`/equipment/${r.itemId}`}>{r.name}</Link></td>
                      <td className="muted">{r.category}</td>
                      <td>
                        <span className={`badge ${r.state === 'out' ? 'badge-blue' : r.state === 'returned' ? 'badge-green' : ''}`}>{t(`packing.${r.state}`)}</span>
                        {r.status === 'lost' && <span style={{ marginLeft: 6 }}><ItemStatusBadge status="lost" /></span>}
                      </td>
                      <td className="right">
                        {!closed && !g.kitId && r.state === 'pending' && (
                          <button className="btn btn-sm btn-ghost" onClick={() => removeItem.mutate(r.itemId)} disabled={removeItem.isPending}>{t('common.remove')}</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}
      {booking && <BookModal e={e} onClose={() => setBooking(false)} onDone={invalidate} />}
    </div>
  );
}

function BookModal({ e, onClose, onDone }: { e: EventDetail; onClose: () => void; onDone: () => void }) {
  const t = useI18n().t;
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('');
  const [kitIds, setKitIds] = useState<Set<string>>(new Set());
  const [itemIds, setItemIds] = useState<Set<string>>(new Set());
  const kits = useQuery({ queryKey: ['kits'], queryFn: () => api.get<Kit[]>('/kits') });
  const available = useQuery({
    queryKey: ['availability', e.id],
    queryFn: () => api.get<Item[]>(`/items/availability${qs({ from: e.setupStart, to: e.teardownEnd })}`),
  });
  const avail = available.data ?? [];
  const availIds = useMemo(() => new Set(avail.map((i) => i.id)), [avail]);
  const categories = useMemo(() => [...new Set(avail.map((i) => i.category))].sort(), [avail]);
  const shown = avail.filter(
    (i) => (!category || i.category === category) && (!search || `${i.qrCode} ${i.name}`.toLowerCase().includes(search.toLowerCase())),
  );
  const book = useMutation({
    mutationFn: () => api.post<{ booked: number }>(`/events/${e.id}/bookings`, { kitIds: [...kitIds], itemIds: [...itemIds] }),
    onSuccess: () => {
      onDone();
      onClose();
    },
  });
  const toggle = (set: Set<string>, setter: (s: Set<string>) => void, id: string) => {
    const n = new Set(set);
    if (n.has(id)) n.delete(id);
    else n.add(id);
    setter(n);
  };
  return (
    <Modal
      wide
      title={t('events.bookEquipment')}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>{t('common.cancel')}</button>
          <button className="btn btn-primary" disabled={(!kitIds.size && !itemIds.size) || book.isPending} onClick={() => book.mutate()}>
            {t('events.bookN', { cases: kitIds.size, items: itemIds.size })}
          </button>
        </>
      }
    >
      <p className="muted" style={{ margin: 0 }}>{t('events.bookHint')}</p>
      <ErrorBox error={book.error} />
      <h3>{t('nav.kits')}</h3>
      {(kits.data ?? []).length === 0 ? (
        <span className="muted small">{t('kits.emptyTitle')}</span>
      ) : (
        <div className="row">
          {(kits.data ?? []).map((k) => (
            <label key={k.id} className="checkbox badge" style={{ padding: '6px 10px', background: kitIds.has(k.id) ? 'var(--ink)' : undefined, color: kitIds.has(k.id) ? '#fff' : undefined }}>
              <input type="checkbox" checked={kitIds.has(k.id)} onChange={() => toggle(kitIds, setKitIds, k.id)} />
              {k.name} · {k.itemCount}
            </label>
          ))}
        </div>
      )}
      <h3>{t('events.singleItems')}</h3>
      <div className="row">
        <input className="input" style={{ flex: 1, minWidth: 200 }} type="search" placeholder={t('equipment.search')} value={search} onChange={(ev) => setSearch(ev.target.value)} />
        <select className="select" style={{ width: 'auto' }} value={category} onChange={(ev) => setCategory(ev.target.value)} aria-label={t('equipment.category')}>
          <option value="">{t('equipment.allCategories')}</option>
          {categories.map((c) => <option key={c}>{c}</option>)}
        </select>
        <button className="btn btn-sm" onClick={() => setItemIds(new Set([...itemIds, ...shown.map((i) => i.id)]))} disabled={!shown.length}>
          {t('common.selectAllN', { n: shown.length })}
        </button>
      </div>
      <div className="table-wrap" style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid var(--line)', borderRadius: 8 }}>
        {available.isLoading ? (
          <Loading />
        ) : shown.length === 0 ? (
          <div className="empty">{t('events.nothingAvailable')}</div>
        ) : (
          <table className="table">
            <tbody>
              {shown.slice(0, 400).map((i) => (
                <tr key={i.id} className="clickable" onClick={() => toggle(itemIds, setItemIds, i.id)}>
                  <td className="check"><input type="checkbox" checked={itemIds.has(i.id)} onChange={() => toggle(itemIds, setItemIds, i.id)} onClick={(ev) => ev.stopPropagation()} aria-label={i.qrCode} /></td>
                  <td className="mono">{i.qrCode}</td>
                  <td>{i.name}</td>
                  <td className="muted">{i.category}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {itemIds.size > 0 && [...itemIds].some((x) => !availIds.has(x)) && <div className="alert alert-warn">{t('events.someUnavailable')}</div>}
    </Modal>
  );
}

function CrewTab({ e }: { e: EventDetail }) {
  const t = useI18n().t;
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [warning, setWarning] = useState<string | null>(null);
  const closed = ['completed', 'invoiced', 'cancelled'].includes(e.status);
  const remove = useMutation({
    mutationFn: (userId: string) => api.del(`/events/${e.id}/crew/${userId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['event', e.id] });
      qc.invalidateQueries({ queryKey: ['events'] });
    },
  });
  return (
    <div className="stack">
      <div className="spread">
        <p className="muted" style={{ margin: 0 }}>{e.requiresLead ? t('events.crewHintLead') : t('events.crewHint')}</p>
        {!closed && (
          <button className="btn btn-primary" onClick={() => setAdding(true)}>
            <Icon.plus />
            {t('events.addCrew')}
          </button>
        )}
      </div>
      {warning && <div className="alert alert-warn">{warning}</div>}
      <ErrorBox error={remove.error} />
      <div className="card table-wrap">
        {e.crew.length === 0 ? (
          <Empty title={t('events.noCrew')} />
        ) : (
          <table className="table">
            <tbody>
              {e.crew.map((c) => (
                <tr key={c.userId}>
                  <td><strong>{c.name}</strong></td>
                  <td>{c.role === 'lead' ? <span className="badge badge-ink">{t('crewRole.lead')}</span> : <span className="badge">{t('crewRole.technician')}</span>}</td>
                  <td>{c.phone && <a href={`tel:${c.phone}`}>{c.phone}</a>}</td>
                  <td className="right">
                    {!closed && <button className="btn btn-sm btn-ghost" onClick={() => remove.mutate(c.userId)} disabled={remove.isPending}>{t('common.remove')}</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {adding && <AddCrewModal e={e} onClose={() => setAdding(false)} onWarning={setWarning} />}
    </div>
  );
}

function AddCrewModal({ e, onClose, onWarning }: { e: EventDetail; onClose: () => void; onWarning: (w: string | null) => void }) {
  const { t, lang } = useI18n();
  const qc = useQueryClient();
  const users = useQuery({ queryKey: ['users'], queryFn: () => api.get<User[]>('/users') });
  const [userId, setUserId] = useState('');
  const [role, setRole] = useState<'lead' | 'technician'>('technician');
  const onCrew = new Set(e.crew.map((c) => c.userId));
  const candidates = (users.data ?? []).filter((u) => u.active && !onCrew.has(u.id)).sort((a, b) => (a.role === 'lead' ? -1 : 0) - (b.role === 'lead' ? -1 : 0));
  const chosen = candidates.find((u) => u.id === userId);
  const add = useMutation({
    mutationFn: () =>
      api.post<{ name: string; warnings?: { doubleBooked: { eventTitle: string; setupStart: string }[] } }>(`/events/${e.id}/crew`, { userId, role }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['event', e.id] });
      qc.invalidateQueries({ queryKey: ['events'] });
      const db = res.warnings?.doubleBooked;
      onWarning(db?.length ? t('events.doubleBooked', { name: res.name, events: db.map((d) => `${d.eventTitle} (${fmtDate(d.setupStart, lang)})`).join(', ') }) : null);
      onClose();
    },
  });
  return (
    <Modal
      title={t('events.addCrew')}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>{t('common.cancel')}</button>
          <button className="btn btn-primary" disabled={!userId || add.isPending} onClick={() => add.mutate()}>{t('common.add')}</button>
        </>
      }
    >
      <ErrorBox error={add.error} />
      <Field label={t('crew.person')} htmlFor="c-user">
        <select id="c-user" className="select" value={userId} onChange={(ev) => { setUserId(ev.target.value); setRole('technician'); }}>
          <option value="">{t('common.choose')}</option>
          {candidates.map((u) => (
            <option key={u.id} value={u.id}>{u.name} — {t(`role.${u.role}`)}</option>
          ))}
        </select>
      </Field>
      <Field label={t('crew.roleOnJob')} htmlFor="c-role">
        <select id="c-role" className="select" value={role} onChange={(ev) => setRole(ev.target.value as 'lead' | 'technician')}>
          <option value="technician">{t('crewRole.technician')}</option>
          <option value="lead" disabled={chosen?.role === 'technician'}>{t('crewRole.lead')}</option>
        </select>
      </Field>
      {chosen?.certifications && chosen.certifications.length > 0 && (
        <div className="small muted">{t('crew.certified')}: {chosen.certifications.map((c) => c.system).join(', ')}</div>
      )}
    </Modal>
  );
}

function HoursTab({ eventId }: { eventId: string }) {
  const { t, lang } = useI18n();
  const q = useQuery({ queryKey: ['time-entries', { eventId }], queryFn: () => api.get<TimeEntry[]>(`/time-entries${qs({ eventId })}`) });
  const rows = q.data ?? [];
  const total = rows.reduce((a, r) => a + (r.hours ?? 0), 0);
  return (
    <div className="stack">
      <div className="spread">
        <p className="muted" style={{ margin: 0 }}>{t('events.hoursHint')}</p>
        <Link className="btn" to={`/timesheets?eventId=${eventId}`}>{t('events.openInTimesheets')}</Link>
      </div>
      <div className="card table-wrap">
        {q.isLoading ? (
          <Loading />
        ) : rows.length === 0 ? (
          <Empty title={t('timesheets.empty')} />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>{t('timesheets.technician')}</th>
                <th>{t('timesheets.in')}</th>
                <th>{t('timesheets.out')}</th>
                <th className="right">{t('timesheets.hours')}</th>
                <th>{t('timesheets.location')}</th>
                <th>{t('common.status')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td><strong>{r.userName}</strong></td>
                  <td className="num nowrap">{fmtDateTime(r.clockInAt, lang)}</td>
                  <td className="num nowrap">{r.clockOutAt ? fmtTime(r.clockOutAt, lang) : <span className="badge badge-blue">{t('timesheets.onSite')}</span>}</td>
                  <td className="right num">{fmtHours(r.hours)}</td>
                  <td className="small">
                    {r.isManual ? t('timesheets.manual') : `${r.clockInVerified ? '✓' : '✗'} ${fmtDistance(r.clockInDistanceM)}`}
                  </td>
                  <td><EntryStatusBadge status={r.status} /></td>
                </tr>
              ))}
              <tr>
                <td colSpan={3}><strong>{t('timesheets.total')}</strong></td>
                <td className="right num"><strong>{fmtHours(total)}</strong></td>
                <td colSpan={2} />
              </tr>
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function ScansTab({ eventId }: { eventId: string }) {
  const { t, lang } = useI18n();
  const q = useQuery({
    queryKey: ['scans', eventId],
    queryFn: () => api.get<{ id: string; type: string; scannedAt: string; qrCode: string; itemName: string; by: string }[]>(`/events/${eventId}/scans`),
  });
  const rows = q.data ?? [];
  return (
    <div className="card table-wrap">
      {q.isLoading ? (
        <Loading />
      ) : rows.length === 0 ? (
        <Empty title={t('events.noScans')} text={t('events.noScansText')} />
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th>{t('events.when')}</th>
              <th>{t('events.scanType')}</th>
              <th>{t('equipment.code')}</th>
              <th>{t('equipment.name')}</th>
              <th>{t('events.by')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="num nowrap">{fmtDateTime(r.scannedAt, lang)}</td>
                <td><span className="badge">{t(`scan.${r.type}`)}</span></td>
                <td className="mono">{r.qrCode}</td>
                <td>{r.itemName}</td>
                <td className="muted">{r.by}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
